// [PURPOSE]: Modal form for submitting bug reports, feedback, and questions
// [WHY]: Users need a way to report issues from within the app
// [INTEGRATION]: Opened by SupportSection, submits to /api/submit-bug-report
// [CREATED]: v1.1.0 — Support & Feedback system

import React, { useState, useEffect, useRef } from 'react';
import { Bug, MessageSquare, HelpCircle, Send, Paperclip, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUI } from '../contexts/UIContext';
import { useCharacter } from '../contexts/CharacterContext';
import { useInventory } from '../contexts/InventoryContext';
import useIsMobile from '../hooks/useIsMobile';
import { APP_VERSION } from '../constants/version';
import MobileModal from './MobileModal';

// [PURPOSE]: Category configuration — icons, labels, placeholders
const CATEGORIES = [
  {
    id: 'bug',
    label: 'Bug',
    icon: Bug,
    placeholder: 'Describe what went wrong. What did you expect to happen vs. what actually happened?',
  },
  {
    id: 'feedback',
    label: 'Feedback',
    icon: MessageSquare,
    placeholder: 'Share your thoughts — what do you like, what could be better, or what feature would you love to see?',
  },
  {
    id: 'question',
    label: 'Question',
    icon: HelpCircle,
    placeholder: 'What do you need help with? Be as specific as you can and we\'ll do our best to help.',
  },
];

/**
 * [PURPOSE]: Bug report / feedback submission dialog
 * [UX]: Dark-themed modal matching app design, category pills, opt-in character data
 *
 * @param {boolean} isOpen - Whether dialog is visible
 * @param {Function} onClose - Callback to close dialog
 * @param {string} initialCategory - 'bug', 'feedback', or 'question'
 */
const BugReportDialog = ({ isOpen, onClose, initialCategory = 'bug' }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const { showStatus } = useUI();
  const { playerStats } = useCharacter();
  const { inventory } = useInventory();
  const isMobile = useIsMobile();
  const titleRef = useRef(null);

  // [FORM STATE]
  const [category, setCategory] = useState(initialCategory);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [includeCharacterData, setIncludeCharacterData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // [UX]: Reset form when dialog opens, pre-fill email if authenticated
  useEffect(() => {
    if (isOpen) {
      setCategory(initialCategory);
      setTitle('');
      setDescription('');
      setStepsToReproduce('');
      setContactEmail(currentUser?.email || '');
      setIncludeCharacterData(false);
      setLoading(false);
      setError('');
      setSubmitted(false);
      // Auto-focus title after render
      setTimeout(() => titleRef.current?.focus(), 100);
    }
  }, [isOpen, initialCategory, currentUser?.email]);

  if (!isOpen) return null;

  // ========================================
  // DATA COLLECTION
  // ========================================

  // [PURPOSE]: Auto-collect environment info (mirrors ErrorBoundary pattern)
  const collectEnvironment = () => {
    let characterCount = 0;
    try {
      const saved = localStorage.getItem('tabletopInventory_savedCharacters');
      characterCount = saved ? JSON.parse(saved).length : 0;
    } catch (e) { /* ignore */ }

    return {
      appVersion: APP_VERSION || 'unknown',
      userAgent: navigator.userAgent,
      platform: /Android/i.test(navigator.userAgent) ? 'android'
        : /iPhone|iPad/i.test(navigator.userAgent) ? 'ios'
        : 'web',
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      isMobile,
      characterCount,
    };
  };

  // [PURPOSE]: Opt-in character data summary (not full JSON dump)
  const collectCharacterData = () => {
    if (!includeCharacterData || !playerStats) return null;
    return {
      name: playerStats.name || 'Unnamed',
      level: playerStats.level || 1,
      classes: (playerStats.classes || []).map(c => ({
        className: c.className,
        level: c.level,
        subclass: c.subclass || null,
      })),
      race: playerStats.basics?.race || 'Unknown',
      inventoryItemCount: inventory?.length || 0,
      inventoryItems: (inventory || []).slice(0, 50).map(i => ({
        name: i.name,
        category: i.category,
        baseType: i.baseType,
      })),
    };
  };

  // ========================================
  // HANDLERS
  // ========================================

  const handleSubmit = async () => {
    // [VALIDATE]: Required fields
    if (!title.trim()) {
      setError('Please enter a title.');
      return;
    }
    if (!description.trim()) {
      setError('Please describe the issue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const headers = { 'Content-Type': 'application/json' };

      // [AUTH]: Include token if authenticated (optional — anonymous allowed)
      if (isAuthenticated && currentUser) {
        try {
          const idToken = await currentUser.getIdToken();
          headers['Authorization'] = `Bearer ${idToken}`;
        } catch (e) {
          // Token fetch failed — submit anonymously
        }
      }

      const response = await fetch('/api/submit-bug-report', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          category,
          title: title.trim(),
          description: description.trim(),
          stepsToReproduce: stepsToReproduce.trim() || null,
          contactEmail: contactEmail.trim() || null,
          environment: collectEnvironment(),
          characterData: collectCharacterData(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit report');
      }

      setSubmitted(true);
      showStatus('Report submitted! Thank you for your feedback.', 4000);
    } catch (err) {
      setError(err.message || 'Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // RENDER
  // ========================================

  const activeCat = CATEGORIES.find(c => c.id === category) || CATEGORIES[0];

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      overlayClassName="z-[60]"
      showSaveButton={false}
      ariaLabel={submitted ? 'Report Submitted' : `Submit ${activeCat.label}`}
      title={
        <span className="flex items-center gap-2">
          <activeCat.icon size={20} className="text-blue-400" />
          {submitted ? 'Report Submitted' : `Submit ${activeCat.label}`}
        </span>
      }
    >
      <div className="space-y-4">
          {submitted ? (
            /* ─── Success State ─── */
            <div className="text-center py-8">
              <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
              <p className="text-white font-medium text-lg mb-2">Thank you!</p>
              <p className="text-gray-400 text-sm mb-6">
                Your {category === 'bug' ? 'bug report' : category} has been submitted.
                {contactEmail.trim() && ' We may reach out if we need more details.'}
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            /* ─── Form ─── */
            <>
              {/* Error banner */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-900/40 border border-red-700 rounded-lg text-red-300 text-sm">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Category pills */}
              <div className="flex gap-2">
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const isActive = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      <Icon size={14} />
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  ref={titleRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 200))}
                  placeholder="Brief summary of the issue"
                  className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none placeholder-gray-500"
                  maxLength={200}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">{title.length}/200</div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 5000))}
                  placeholder={activeCat.placeholder}
                  rows={4}
                  className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none placeholder-gray-500 resize-y"
                  maxLength={5000}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">{description.length}/5000</div>
              </div>

              {/* Steps to Reproduce (bug category only) */}
              {category === 'bug' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Steps to Reproduce <span className="text-gray-500">(optional)</span>
                  </label>
                  <textarea
                    value={stepsToReproduce}
                    onChange={(e) => setStepsToReproduce(e.target.value.slice(0, 2000))}
                    placeholder={"1. Go to...\n2. Click on...\n3. See error..."}
                    rows={3}
                    className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none placeholder-gray-500 resize-y"
                    maxLength={2000}
                  />
                </div>
              )}

              {/* Contact email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email <span className="text-gray-500">(optional — only if you'd like a response)</span>
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none placeholder-gray-500"
                />
              </div>

              {/* Include character data checkbox */}
              <label className="flex items-start gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                <input
                  type="checkbox"
                  checked={includeCharacterData}
                  onChange={(e) => setIncludeCharacterData(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-600"
                />
                <div>
                  <div className="text-sm text-gray-200 flex items-center gap-1.5">
                    <Paperclip size={14} className="text-gray-400" />
                    Include character data
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Attaches your current character's name, class, level, and inventory summary to help with debugging.
                  </div>
                </div>
              </label>

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                {loading ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Submit {activeCat.label}
                  </>
                )}
              </button>

              {/* Auto-collected notice */}
              <p className="text-xs text-gray-600 text-center">
                Your app version, browser, and screen size are automatically included to help with debugging.
              </p>
            </>
          )}
      </div>
    </MobileModal>
  );
};

export default BugReportDialog;
